import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Clock, FileSpreadsheet, Building2 } from 'lucide-react';

const metrics = [
  {
    icon: TrendingUp,
    value: 18,
    suffix: '%',
    prefix: '+',
    label: 'Increase in Direct Bookings',
    description: 'Average improvement across all clients',
  },
  {
    icon: Clock,
    value: 2.4,
    suffix: 'x',
    prefix: '',
    label: 'Faster Month-End Reporting',
    description: 'Reduce reporting time dramatically',
  },
  {
    icon: FileSpreadsheet,
    value: 27,
    suffix: '%',
    prefix: '-',
    label: 'Time on Manual Spreadsheets',
    description: 'Automate data collection & analysis',
  },
  {
    icon: Building2,
    value: 500,
    suffix: '+',
    prefix: '',
    label: 'Hotels Trust Prowen',
    description: 'Across 40+ countries worldwide',
  },
];

function AnimatedCounter({ value, suffix, prefix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current * 10) / 10);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value, duration]);

  return (
    <span ref={ref} className="ticker">
      {prefix}{Number.isInteger(value) ? Math.floor(count) : count.toFixed(1)}{suffix}
    </span>
  );
}

export default function MetricsSection() {
  return (
    <section className="py-20 lg:py-28 section-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">
            Proven Results
          </p>
          <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Impact You Can Measure in Weeks
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Real results from real hotels using Prowen Analytics.
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {metrics.map((metric, index) => (
            <Card 
              key={index} 
              className="card-hover bg-card border-border/50 text-center overflow-hidden group"
            >
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <metric.icon className="w-7 h-7 text-primary" />
                  </div>
                </div>
                <div className="text-4xl lg:text-5xl font-bold text-foreground mb-3 font-heading">
                  <AnimatedCounter 
                    value={metric.value} 
                    suffix={metric.suffix} 
                    prefix={metric.prefix} 
                  />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {metric.label}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {metric.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Logos */}
        {/* <div className="mt-20">
          <p className="text-center text-sm text-muted-foreground mb-8">
            Trusted by leading hotel brands and operators worldwide
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16 opacity-60">
            {['Marriott', 'Hilton', 'IHG', 'Hyatt', 'Accor', 'Wyndham'].map((brand, index) => (
              <div 
                key={index} 
                className="text-lg font-semibold text-muted-foreground/80 tracking-wider uppercase"
              >
                {brand}
              </div>
            ))}
          </div>
        </div> */}
      </div>
    </section>
  );
}
